import React from 'react';
import { useFormContext } from 'react-hook-form';
import Label from '../atoms/Label';
import Input from '../atoms/Input';
import { cn } from '../utils/cn';
import { sanitizeText, sanitizeEmail, sanitizeNumber } from '../../utils/sanitize';

/**
 * FormField Component - Molecule
 * Combines Label and Input atoms with react-hook-form integration
 * 
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.name - Input name (required for react-hook-form)
 * @param {string} props.error - Error message (optional, can be from react-hook-form)
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.required - Required field
 * @param {string} props.sanitizeType - Type of sanitization: 'text' | 'email' | 'number'
 */
const FormField = React.forwardRef(
  (
    {
      label,
      name,
      error: externalError,
      helperText,
      required = false,
      className = '',
      labelClassName = '',
      sanitizeType = 'text',
      type, // Explicitly extract type to ensure it's not overridden
      leftIcon,
      rightIcon,
      ...inputProps
    },
    ref
  ) => {
    // Try to get form context (if used within react-hook-form)
    let formContext = null;
    try {
      formContext = useFormContext();
    } catch (e) {
      // Not within a form context, use external error
    }

    // Safely extract form methods and errors
    const register = formContext?.register;
    const formState = formContext?.formState;
    const errors = formState?.errors || {};

    // Get error from form context or external prop
    const error = formContext && errors[name]
      ? errors[name]?.message 
      : externalError;

    // Sanitize input based on type - runs on every input event
    const handleChange = (e) => {
      if (!e || !e.target) return;
      
      const value = e.target.value || '';
      let sanitized = value;

      switch (sanitizeType) {
        case 'email':
          sanitized = sanitizeEmail(value);
          break;
        case 'number':
          sanitized = sanitizeNumber(value)?.toString() || '';
          break;
        default:
          sanitized = sanitizeText(value);
      }

      // Always update the input value with sanitized content if it changed
      if (sanitized !== value && e.target) {
        e.target.value = sanitized;
        // Trigger input event to ensure React state updates
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(e.target, sanitized);
          const event = new Event('input', { bubbles: true });
          e.target.dispatchEvent(event);
        }
      }

      // Call original onChange if provided, with sanitized value
      if (inputProps.onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: sanitized
          }
        };
        inputProps.onChange(syntheticEvent);
      }
    };

    // Handle paste events to sanitize pasted content
    const handlePaste = (e) => {
      e.preventDefault();
      const pastedText = (e.clipboardData || window.clipboardData).getData('text');
      let sanitized = pastedText;

      switch (sanitizeType) {
        case 'email':
          sanitized = sanitizeEmail(pastedText);
          break;
        case 'number':
          sanitized = sanitizeNumber(pastedText)?.toString() || '';
          break;
        default:
          sanitized = sanitizeText(pastedText);
      }

      // Insert sanitized text at cursor position
      const input = e.target;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;
      const newValue = currentValue.substring(0, start) + sanitized + currentValue.substring(end);
      
      input.value = newValue;
      input.setSelectionRange(start + sanitized.length, start + sanitized.length);

      // Trigger change event
      const event = new Event('input', { bubbles: true });
      input.dispatchEvent(event);
      
      if (inputProps.onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...input,
            value: newValue
          }
        };
        inputProps.onChange(syntheticEvent);
      }
    };

    // Handle blur to sanitize on field exit
    const handleBlur = (e) => {
      const value = e.target.value;
      let sanitized = value;

      switch (sanitizeType) {
        case 'email':
          sanitized = sanitizeEmail(value);
          break;
        case 'number':
          sanitized = sanitizeNumber(value)?.toString() || '';
          break;
        default:
          sanitized = sanitizeText(value);
      }

      if (sanitized !== value && e.target) {
        e.target.value = sanitized;
        const event = new Event('input', { bubbles: true });
        e.target.dispatchEvent(event);
      }

      if (inputProps.onBlur) {
        inputProps.onBlur(e);
      }
    };

    // If within form context, use register
    const inputRegister = register 
      ? {
          ...register(name, {
            onChange: handleChange,
            onBlur: handleBlur,
            ...inputProps
          }),
          onPaste: handlePaste,
          // Ensure type is explicitly set and not overridden by register
          ...(type && { type })
        }
      : {
          onChange: handleChange,
          onBlur: handleBlur,
          onPaste: handlePaste,
          ...inputProps,
          // Ensure type is explicitly set
          ...(type && { type })
        };

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label
            htmlFor={name}
            required={required}
            className={labelClassName}
          >
            {label}
          </Label>
        )}
        <Input
          ref={ref}
          id={name}
          name={name}
          variant={error ? 'error' : 'default'}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error || helperText
              ? `${name}-${error ? 'error' : 'helper'}`
              : undefined
          }
          onPaste={handlePaste}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          {...inputRegister}
        />
        {error && (
          <p
            id={`${name}-error`}
            className="text-sm text-error-600 dark:text-error-400 font-medium"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p
            id={`${name}-helper`}
            className="text-sm text-primary-500 dark:text-primary-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
